/**
 * M16A — Home por Perfil / Painel do Usuário
 * Rota: "/"
 *
 * Esta página substitui o Overview/Painel Geral como landing page autenticada.
 * O conteúdo é resolvido dinamicamente pelo UserHomeService com base no perfil
 * e permissões reais do usuário logado (não depende de activeClientId).
 *
 * Perfis suportados:
 * - INSPECTOR → Dashboard do Inspetor
 * - ENGINEER → Central de Trabalho da Engenharia
 * - MANAGER → Central Gerencial
 * - ADMIN → Central Administrativa
 * - DEVELOPER → Central Técnica do Sistema
 * - Outros → Home genérica baseada em permissões
 */

import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../domain/identity/access-types.js";

type HomeProfile = "INSPECTOR" | "ENGINEER" | "MANAGER" | "ADMIN" | "DEVELOPER" | "GENERIC";

interface UserHomeContext {
  profile: HomeProfile;
  userId: string;
  userName: string;
  roleDisplay: string;
  professionalId: string | null;
  permissions: string[];
  canViewIndicators: boolean;
}

interface ProfessionalInfo {
  name: string;
  functionTitle: string;
  cft: string | null;
  crea: string | null;
  snqc: string | null;
  sncc: string | null;
  endIso: string | null;
  endIso9712: string | null;
  signatureImageId: string | null;
  qualifications: Array<{ authority: string; title: string; registrationNumber: string; validUntil: string | null }>;
}

interface InspectorDashboardData {
  professional: ProfessionalInfo | null;
  reportCounts: { total: number; inElaboration: number; completed: number };
  fieldScope: {
    rows: Array<{
      lineEquipmentId: string;
      lineTag: string;
      systemId: string | null;
      systemTag: string;
      fluid: string | null;
      from: string | null;
      to: string | null;
      progress: { progressPercent: number };
      status: string;
    }>;
    summary: { totalLines: number; completedLines: number; overallPercent: number };
    systems: Array<{ id: string; tag: string }>;
  } | null;
  subOsList: Array<{ id: string; number: string; status: string; projectName: string | null; clientName: string | null }>;
}

/** HTML-escape for any user- or database-originated text placed in templates. */
function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default: return "&#39;";
    }
  });
}

class AppUserHomePage extends HTMLElement {
  private ctx: UserHomeContext | null = null;
  private inspectorData: InspectorDashboardData | null = null;
  private ownPhotoDataUrl: string | null = null;
  private loading = true;
  private error: string | null = null;

  async connectedCallback() {
    await this.loadContext();
    this.render();
  }

  private async loadContext(): Promise<void> {
    try {
      const platform = (window as any).app?.platform;
      if (!platform?.userHomeService) {
        throw new Error("UserHomeService não disponível na plataforma.");
      }

      // M16A HOME-RUNTIME-02: repassar contexto do contextStore para o serviço.
      const session = contextStore.get();
      this.ctx = await platform.userHomeService.load({
        userId: session.userId,
        userName: session.userName,
        role: session.role,
        roleDisplay: session.roleDisplay,
        professionalId: session.professionalId ?? null,
        permissions: session.permissions ? Array.from(session.permissions) : []
      });

      // M16A HOME-FINAL-03: carregar dados operacionais reais via RPC quando
      // o perfil for INSPECTOR. Em BrowserPlatform (npm run dev), a instância
      // local não tem repositórios — chamamos o método diretamente mas ele
      // retorna dados vazios. A UI deve tratar isso gracefully.
      if (this.ctx && this.ctx.profile === "INSPECTOR") {
        // Foto do próprio usuário via RPC autenticada; nunca a assinatura técnica.
        try {
          const ownPhoto = await platform.adminService?.getOwnProfileImage?.({
            activeClientId: "" as any,
            userId: session.userId,
            permissions: session.permissions || new Set(),
            correlationId: ""
          });
          this.ownPhotoDataUrl = typeof ownPhoto === "string" && /^data:image\/(?:png|jpeg|webp);base64,/i.test(ownPhoto)
            ? ownPhoto
            : null;
        } catch {
          this.ownPhotoDataUrl = null;
        }
        try {
          this.inspectorData = await platform.userHomeService.loadInspectorData({
            activeClientId: "" as any,
            correlationId: "" as any,
            userId: session.userId as any,
            professionalId: (session.professionalId || null) as any,
            permissions: session.permissions || new Set()
          });
        } catch (err: any) {
          // Falha de backend não é ausência de relatórios: nunca exibir zero falso.
          console.warn("[M16A] loadInspectorData falhou:", err?.message);
          this.inspectorData = null;
          this.error = "Não foi possível carregar os indicadores do Inspetor. Tente novamente.";
        }
      }

      this.loading = false;
    } catch (err: any) {
      this.error = err?.message || "Falha ao carregar contexto da Home.";
      this.loading = false;
    }
  }

  private render() {
    if (this.loading) {
      this.innerHTML = `<div style="padding:2rem;text-align:center;color:var(--color-text-secondary,#6b7280);">Carregando…</div>`;
      return;
    }
    if (this.error || !this.ctx) {
      this.innerHTML = `<div style="padding:2rem;text-align:center;color:var(--ia-danger-fg,#F87171);">${escapeHtml(this.error || "Erro desconhecido.")}</div>`;
      return;
    }

    const c = this.ctx;
    const variant = this.renderVariant(c);

    this.innerHTML = `
      <div style="padding: 2rem; max-width: 1400px; margin: 0 auto;">
        ${variant}
      </div>
    `;
  }

  private renderVariant(c: UserHomeContext): string {
    switch (c.profile) {
      case "INSPECTOR":
        return this.renderInspector(c);
      case "ENGINEER":
        return this.renderEngineer(c);
      case "MANAGER":
        return this.renderManager(c);
      case "ADMIN":
        return this.renderAdmin(c);
      case "DEVELOPER":
        return this.renderDeveloper(c);
      default:
        return this.renderGeneric(c);
    }
  }

  /** INSPECTOR — Dashboard completo com dados operacionais reais. */
  private renderInspector(c: UserHomeContext): string {
    const d = this.inspectorData;
    const p = d?.professional;

    // Header profissional
    const avatarHtml = this.ownPhotoDataUrl
      ? `<img src="${this.ownPhotoDataUrl}" alt="Foto de ${escapeHtml(p?.name || c.userName)}" style="width:64px;height:64px;border-radius:50%;object-fit:cover;border:2px solid var(--color-border-subtle,#e5e7eb);" />`
      : `<div style="width:64px;height:64px;border-radius:50%;background:var(--ia-accent-bg,#2563eb);color:#fff;display:flex;align-items:center;justify-content:center;font-size:1.5rem;font-weight:700;">${escapeHtml((c.userName || "U").charAt(0).toUpperCase())}</div>`;

    const certs: string[] = [];
    if (p?.cft) certs.push(`CFT: ${p.cft}`);
    if (p?.crea) certs.push(`CREA: ${p.crea}`);
    if (p?.snqc) certs.push(`SNQC-ABENDI: ${p.snqc}`);
    if (p?.sncc) certs.push(`SNCC-CEBRACI: ${p.sncc}`);
    // Em cadastros legados, endIso/endIso9712 podem ser aliases do SNQC.
    // Não apresentar três certificados distintos para um único registro.
    const baseCertIds = new Set([p?.snqc, p?.sncc, p?.cft, p?.crea].filter(Boolean).map(value => String(value).trim().toUpperCase()));
    if (p?.endIso && !baseCertIds.has(String(p.endIso).trim().toUpperCase())) {
      certs.push(`ISO 9712: ${p.endIso}`);
    }
    if (p?.endIso9712 && !baseCertIds.has(String(p.endIso9712).trim().toUpperCase()) &&
        String(p.endIso9712).trim().toUpperCase() !== String(p.endIso ?? "").trim().toUpperCase()) {
      certs.push(`END ISO 9712: ${p.endIso9712}`);
    }
    if (p?.qualifications) {
      for (const q of p.qualifications) {
        if (q.title && q.registrationNumber) {
          certs.push(`${q.title}: ${q.registrationNumber}`);
        }
      }
    }
    const certsHtml = certs.length > 0
      ? `<div style="display:flex;flex-wrap:wrap;gap:0.5rem;margin-top:0.5rem;">${certs.map(c => `<span style="font-size:0.75rem;background:var(--color-surface-alt,#f3f4f6);border:1px solid var(--color-border-subtle,#e5e7eb);border-radius:4px;padding:0.125rem 0.5rem;color:var(--color-text-secondary,#6b7280);">${escapeHtml(c)}</span>`).join("")}</div>`
      : "";

    // Cards de relatórios
    const counts = d?.reportCounts || { total: 0, inElaboration: 0, completed: 0 };

    // Sistemas e Linhas
    const lines = d?.fieldScope?.rows || [];
    const linesHtml = lines.length > 0
      ? `<table style="width:100%;border-collapse:collapse;font-size:0.875rem;">
          <thead>
            <tr style="border-bottom:2px solid var(--color-border-subtle,#e5e7eb);text-align:left;">
              <th style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);font-weight:600;">Sistema</th>
              <th style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);font-weight:600;">Linha / TAG</th>
              <th style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);font-weight:600;">Fluido</th>
              <th style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);font-weight:600;">De → Para</th>
              <th style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);font-weight:600;">Avanço</th>
              <th style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);font-weight:600;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${lines.map(l => `
              <tr style="border-bottom:1px solid var(--color-border-subtle,#e5e7eb);">
                <td style="padding:0.75rem 0.5rem;color:var(--color-text-primary,#1f2937);">${escapeHtml(l.systemTag || "—")}</td>
                <td style="padding:0.75rem 0.5rem;color:var(--color-text-primary,#1f2937);font-weight:500;">${escapeHtml(l.lineTag || l.lineEquipmentId)}</td>
                <td style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);">${escapeHtml(l.fluid || "—")}</td>
                <td style="padding:0.75rem 0.5rem;color:var(--color-text-secondary,#6b7280);">${escapeHtml(l.from || "?")} → ${escapeHtml(l.to || "?")}</td>
                <td style="padding:0.75rem 0.5rem;">
                  <div style="display:flex;align-items:center;gap:0.5rem;">
                    <div style="flex:1;height:6px;background:var(--color-border-subtle,#e5e7eb);border-radius:3px;overflow:hidden;max-width:100px;">
                      <div style="height:100%;width:${l.progress?.progressPercent || 0}%;background:var(--ia-accent-bg,#2563eb);border-radius:3px;"></div>
                    </div>
                    <span style="font-size:0.75rem;color:var(--color-text-secondary,#6b7280);">${l.progress?.progressPercent || 0}%</span>
                  </div>
                </td>
                <td style="padding:0.75rem 0.5rem;"><span style="font-size:0.75rem;padding:0.125rem 0.5rem;border-radius:4px;background:var(--color-surface-alt,#f3f4f6);color:var(--color-text-secondary,#6b7280);">${escapeHtml(l.status || "—")}</span></td>
              </tr>
            `).join("")}
          </tbody>
        </table>`
      : `<p style="color:var(--color-text-secondary,#6b7280);font-size:0.875rem;padding:1rem 0;">Nenhum sistema ou linha em andamento.</p>`;

    // Sub-OS Vinculadas
    const subOs = d?.subOsList || [];
    const subOsHtml = subOs.length > 0
      ? `<div style="display:flex;flex-direction:column;gap:0.75rem;">
          ${subOs.map(s => `
            <a href="/management/rdo?subOs=${encodeURIComponent(s.id)}" style="display:block;background:var(--color-surface,#fff);border:1px solid var(--color-border-subtle,#e5e7eb);border-radius:8px;padding:1rem;text-decoration:none;transition:box-shadow .15s ease;" onmouseenter="this.style.boxShadow='0 2px 8px rgba(0,0,0,.06)'" onmouseleave="this.style.boxShadow='none'">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.25rem;">
                <span style="font-weight:600;color:var(--color-text-primary,#1f2937);font-size:0.875rem;">${escapeHtml(s.number || s.id)}</span>
                <span style="font-size:0.75rem;padding:0.125rem 0.5rem;border-radius:4px;background:var(--color-surface-alt,#f3f4f6);color:var(--color-text-secondary,#6b7280);">${escapeHtml(s.status || "—")}</span>
              </div>
              ${s.projectName ? `<div style="font-size:0.8rem;color:var(--color-text-secondary,#6b7280);">📁 ${escapeHtml(s.projectName)}</div>` : ""}
              ${s.clientName ? `<div style="font-size:0.8rem;color:var(--color-text-secondary,#6b7280);">🏢 ${escapeHtml(s.clientName)}</div>` : ""}
            </a>
          `).join("")}
        </div>`
      : `<p style="color:var(--color-text-secondary,#6b7280);font-size:0.875rem;">Nenhuma Sub-OS vinculada.</p>`;

    return `
      <!-- HEADER DO INSPETOR -->
      <header style="display:flex;align-items:center;gap:1.5rem;margin-bottom:2rem;padding-bottom:1.5rem;border-bottom:1px solid var(--color-border-subtle,#e5e7eb);">
        <div style="min-width:0;flex:1;">
          <h1 style="font-size:1.75rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">
            ${escapeHtml(p?.name || c.userName)}
          </h1>
          <p style="font-size:0.95rem;color:var(--color-text-secondary,#6b7280);margin:0.25rem 0 0 0;">
            ${escapeHtml(p?.functionTitle || c.roleDisplay)}
          </p>
          ${certsHtml}
        </div>
        <div style="margin-left:auto;flex-shrink:0;">${avatarHtml}</div>
      </header>

      <!-- TRÊS INDICADORES -->
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:1.5rem;margin-bottom:2rem;">
        ${this.metricCard("📋 Total de Relatórios", String(counts.total), "/reports")}
        ${this.metricCard("✏️ Em Elaboração", String(counts.inElaboration), "/reports?status=DRAFT")}
        ${this.metricCard("✅ Concluídos", String(counts.completed), "/reports?status=COMPLETED")}
      </div>

      <!-- ÁREA PRINCIPAL + LATERAL -->
      <div style="display:grid;grid-template-columns:1fr 320px;gap:2rem;">
        <!-- SISTEMAS E LINHAS EM ANDAMENTO -->
        <section>
          <h2 style="font-size:1.25rem;font-weight:600;color:var(--color-text-primary,#1f2937);margin:0 0 1rem 0;">
            Sistemas e Linhas em Andamento
          </h2>
          ${linesHtml}
        </section>

        <!-- SUB-OS VINCULADAS -->
        <aside>
          <h2 style="font-size:1.1rem;font-weight:600;color:var(--color-text-primary,#1f2937);margin:0 0 1rem 0;">
            Sub-OS Vinculadas
          </h2>
          ${subOsHtml}
        </aside>
      </div>

      <style>
        @media (max-width: 900px) {
          div[style*="grid-template-columns:1fr 320px"] {
            grid-template-columns: 1fr !important;
          }
        }
      </style>
    `;
  }

  /** ENGINEER — Central de Trabalho da Engenharia. */
  private renderEngineer(c: UserHomeContext): string {
    const cta = c.canViewIndicators
      ? `<a href="/dashboard/indicators" style="display:inline-block;margin-top:1rem;padding:0.5rem 1rem;background:var(--ia-accent-bg,#2563eb);color:#fff;border-radius:6px;text-decoration:none;font-size:0.875rem;">Painel de Avanços e Indicadores →</a>`
      : "";
    return `
      <header style="margin-bottom:2rem;">
        <h1 style="font-size:1.75rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">Bem-vindo, ${escapeHtml(c.userName)}</h1>
        <p style="font-size:0.95rem;color:var(--color-text-secondary,#6b7280);margin-top:0.25rem;">Perfil: <strong>${escapeHtml(c.roleDisplay)}</strong></p>
      </header>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:1.5rem;">
        ${this.card("⏳ Pendências para mim", "Documentos aguardando sua análise como PLH.", "/engineering?tab=revisao&mine=true")}
        ${this.card("🔍 Aguardando Análise", "Fila de revisão técnica pendente.", "/engineering?tab=revisao")}
        ${this.card("📝 Minutas Pendentes", "Minutas de recomendações para aprovação.", "/integrity/recommendations-draft")}
        ${this.card("🚫 HOLD / Alertas", "Documentos em hold ou com bloqueios ativos.", "/engineering?tab=hold")}
        ${this.card("📂 Fila de Trabalho", "Todos os documentos na fila de engenharia.", "/engineering")}
        ${this.card("🕘 Atividade Recente", "Histórico de revisões e correções.", "/engineering?tab=historico")}
      </div>
      <div style="margin-top:1.5rem;">${cta}</div>
    `;
  }

  /** MANAGER — Central Gerencial. */
  private renderManager(c: UserHomeContext): string {
    const cta = c.canViewIndicators
      ? `<a href="/dashboard/indicators" style="display:inline-block;margin-top:1rem;padding:0.5rem 1rem;background:var(--ia-accent-bg,#2563eb);color:#fff;border-radius:6px;text-decoration:none;font-size:0.875rem;">Painel de Avanços e Indicadores →</a>`
      : "";
    return `
      <header style="margin-bottom:2rem;">
        <h1 style="font-size:1.75rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">Bem-vindo, ${escapeHtml(c.userName)}</h1>
        <p style="font-size:0.95rem;color:var(--color-text-secondary,#6b7280);margin-top:0.25rem;">Perfil: <strong>${escapeHtml(c.roleDisplay)}</strong></p>
      </header>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:1.5rem;">
        ${this.card("📊 Projetos Ativos", "Projetos em execução sob gestão.", "/management/projects")}
        ${this.card("🔧 Inspeções em Andamento", "Inspeções abertas nas frentes de serviço.", "/inspections")}
        ${this.card("✏️ Relatórios em Elaboração", "RIs em rascunho ou retornados.", "/reports?status=DRAFT")}
        ${this.card("✅ Relatórios Concluídos", "RIs finalizados no período.", "/reports?status=COMPLETED")}
        ${this.card("⚠️ Pendências Críticas", "Itens que exigem ação gerencial imediata.", "/management/os")}
        ${this.card("🔴 Recomendações Críticas", "Recomendações de integridade abertas.", "/integrity/recommendations")}
      </div>
      <div style="margin-top:1.5rem;">${cta}</div>
    `;
  }

  /** ADMIN — Central Administrativa. */
  private renderAdmin(c: UserHomeContext): string {
    return `
      <header style="margin-bottom:2rem;">
        <h1 style="font-size:1.75rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">Bem-vindo, ${escapeHtml(c.userName)}</h1>
        <p style="font-size:0.95rem;color:var(--color-text-secondary,#6b7280);margin-top:0.25rem;">Perfil: <strong>${escapeHtml(c.roleDisplay)}</strong></p>
      </header>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:1.5rem;">
        ${this.card("🏢 Clientes", "Cadastro e gestão de clientes.", "/management/clients")}
        ${this.card("📁 Projetos", "Projetos cadastrados no sistema.", "/management/projects")}
        ${this.card("👥 Usuários", "Contas de acesso ativas.", "/administration/people")}
        ${this.card("🎓 Profissionais", "Profissionais e habilitações.", "/administration/people")}
        ${this.card("🔧 Inspeções", "Visão geral das inspeções.", "/inspections")}
        ${this.card("📄 Relatórios Pendentes", "RIs aguardando finalização administrativa.", "/reports")}
      </div>
    `;
  }

  /** DEVELOPER — Central Técnica do Sistema. */
  private renderDeveloper(c: UserHomeContext): string {
    return `
      <header style="margin-bottom:2rem;">
        <h1 style="font-size:1.75rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">Bem-vindo, ${escapeHtml(c.userName)}</h1>
        <p style="font-size:0.95rem;color:var(--color-text-secondary,#6b7280);margin-top:0.25rem;">Perfil: <strong>${escapeHtml(c.roleDisplay)}</strong></p>
      </header>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:1.5rem;">
        ${this.card("ℹ️ Versão e Ambiente", "Informações seguras sobre a instância.", "/settings")}
        ${this.card("💚 Saúde do Backend", "Status dos serviços e conectividade.", "/settings?tab=health")}
        ${this.card("⚙️ Configurações", "Parâmetros funcionais do sistema.", "/settings")}
        ${this.card("🗄️ Backup e Restauração", "Gestão de snapshots do banco.", "/administration/backup")}
        ${this.card("🤖 INSPECT IA REVIEW", "Logs e status da IA de revisão.", "/ai-review")}
      </div>
      <p style="margin-top:1rem;font-size:0.8rem;color:var(--color-text-tertiary,#9ca3af);">
        Tokens, senhas, cookies, connection strings e paths sensíveis nunca são exibidos nesta tela.
      </p>
    `;
  }

  /** GENERIC — Home genérica baseada em permissões. */
  private renderGeneric(c: UserHomeContext): string {
    const links: string[] = [];
    if (c.permissions.includes(PermissionCode.InspectionRead)) links.push(this.card("🔧 Inspeções", "", "/inspections"));
    if (c.permissions.includes(PermissionCode.ReportRead)) links.push(this.card("📄 Relatórios", "", "/reports"));
    if (c.permissions.includes(PermissionCode.EquipmentRead)) links.push(this.card("🏗️ Equipamentos", "", "/equipment"));
    if (c.permissions.includes(PermissionCode.DocumentRead)) links.push(this.card("📚 Documentação", "", "/documentation/library"));
    if (links.length === 0) {
      return `<p style="color:var(--color-text-secondary,#6b7280);">Seu perfil não possui módulos liberados. Entre em contato com o administrador.</p>`;
    }
    return `
      <header style="margin-bottom:2rem;">
        <h1 style="font-size:1.75rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">Bem-vindo, ${escapeHtml(c.userName)}</h1>
        <p style="font-size:0.95rem;color:var(--color-text-secondary,#6b7280);margin-top:0.25rem;">Perfil: <strong>${escapeHtml(c.roleDisplay)}</strong></p>
      </header>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:1.5rem;">${links.join("")}</div>
    `;
  }

  private card(title: string, description: string, href: string): string {
    return `
      <a href="${href}" style="display:block;background:var(--color-surface,#fff);border:1px solid var(--color-border-subtle,#e5e7eb);border-radius:12px;padding:1.5rem;text-decoration:none;transition:box-shadow .15s ease;" onmouseenter="this.style.boxShadow='0 4px 12px rgba(0,0,0,.08)'" onmouseleave="this.style.boxShadow='none'">
        <h3 style="font-size:1rem;font-weight:600;margin:0 0 0.5rem 0;color:var(--color-text-primary,#1f2937);">${title}</h3>
        <p style="font-size:0.875rem;color:var(--color-text-secondary,#6b7280);margin:0;">${description}</p>
      </a>
    `;
  }

  private metricCard(title: string, value: string, href: string): string {
    return `
      <a href="${href}" style="display:block;background:var(--color-surface,#fff);border:1px solid var(--color-border-subtle,#e5e7eb);border-radius:12px;padding:1.5rem;text-decoration:none;transition:box-shadow .15s ease;" onmouseenter="this.style.boxShadow='0 4px 12px rgba(0,0,0,.08)'" onmouseleave="this.style.boxShadow='none'">
        <p style="font-size:0.875rem;color:var(--color-text-secondary,#6b7280);margin:0 0 0.5rem 0;">${title}</p>
        <p style="font-size:2rem;font-weight:700;color:var(--color-text-primary,#1f2937);margin:0;">${value}</p>
      </a>
    `;
  }
}

if (!customElements.get("app-user-home-page")) {
  customElements.define("app-user-home-page", AppUserHomePage);
}